import { Firm } from 'api/interfaces/firm';
import { ModalType } from 'components/TaskRunningStatus/reducer';
import { Reducer } from 'redux';
import { Action } from 'redux/action';

export enum FirmsActions {
  getFirmsStarted = '/actions/firms/get-firms-started',
  getFirmsCompleted = '/actions/firms/get-firms-completed',
  getFirmsFailed = '/actions/firms/get-firms-failed',

  getFirmsByIdStarted = '/actions/firms/get-firms-by-id-started',
  getFirmsByIdCompleted = '/actions/firms/get-firms-by-id-completed',
  getFirmsByIdFailed = '/actions/firms/get-firms-by-id-failed',

  createFirmStarted = '/actions/firms/create-firm-started',
  createFirmCompleted = '/actions/firms/create-firm-completed',
  createFirmFailed = '/actions/firms/create-firm-failed',

  updateFirmStarted = '/actions/firms/update-firm-started',
  updateFirmCompleted = '/actions/firms/update-firm-completed',
  updateFirmFailed = '/actions/firms/update-firm-failed',

  deleteFirmStarted = '/actions/firms/delete-firm-started',
  deleteFirmCompleted = '/actions/firms/delete-firm-completed',
  deleteFirmFailed = '/actions/firms/delete-firm-failed',

  closeTaskModal = '/actions/firms/close-task-modal',
  setSelectedBusinessUnit = '/actions/firms/set-selected-business-unit',
  setIsConnectivity = '/actions/firms/set-is-connectivity',

  setFirmsTypes = '/actions/firms/set-firms-types',
  getFirmsTypesStarted = '/actions/firms/get-firms-types-started',
  getFirmsTypesCompleted = '/actions/firms/get-firms-types-completed',
  getFirmsTypesFailed = '/actions/firms/get-firms-types-failed',
}

export interface FirmsState {
  readonly firms: readonly Firm[];
  readonly selectedBusinessUnit: Record<string, string> | null;
  readonly taskMessage: string | null;
  readonly modalType: ModalType;
  readonly loading: boolean;
  readonly firmsById: Firm[] | null;
  readonly loadingById: boolean;
  readonly isConnectivity: boolean;
  readonly firmsTypes: string[];
  readonly loadingFirmsTypes: boolean;
}

const initialState: FirmsState = {
  firms: [],
  selectedBusinessUnit: null,
  taskMessage: null,
  modalType: ModalType.none,
  loading: false,
  firmsById: [],
  loadingById: false,
  isConnectivity: false,
  firmsTypes: [],
  loadingFirmsTypes: false,
};

const reducer: Reducer<FirmsState, Action<FirmsActions>> = (
  state: FirmsState = initialState,
  action: Action<FirmsActions>
): FirmsState => {
  const { type, data } = action;
  const { firms } = state;

  switch (type) {
    case FirmsActions.getFirmsStarted:
      return { ...state, loading: true };
    case FirmsActions.getFirmsCompleted:
      return { ...state, firms: data, loading: false };
    case FirmsActions.getFirmsFailed:
      return { ...state, loading: false };
    case FirmsActions.getFirmsByIdStarted:
      return { ...state, loadingById: true };
    case FirmsActions.getFirmsByIdCompleted:
      return { ...state, firmsById: data, loadingById: false };
    case FirmsActions.getFirmsByIdFailed:
      return { ...state, loadingById: false };
    case FirmsActions.updateFirmFailed:
    case FirmsActions.deleteFirmFailed:
    case FirmsActions.createFirmFailed:
      return { ...state, taskMessage: data, modalType: ModalType.error };
    case FirmsActions.createFirmStarted:
      return {
        ...state,
        taskMessage: 'Creating firm, please wait',
        modalType: ModalType.progress,
      };
    case FirmsActions.createFirmCompleted:
      return {
        ...state,
        firms: [data, ...firms],
        taskMessage: 'Firm updated successfully',
        modalType: ModalType.success,
      };
    case FirmsActions.updateFirmStarted:
      return {
        ...state,
        taskMessage: 'Updating firm, please wait',
        modalType: ModalType.progress,
      };
    case FirmsActions.updateFirmCompleted:
      return {
        ...state,
        firms: firms.map((firm: Firm): Firm => (firm.firmID === data.firmID ? data : firm)),
        taskMessage: 'Firm updated successfully',
        modalType: ModalType.success,
      };
    case FirmsActions.deleteFirmStarted:
      return {
        ...state,
        taskMessage: 'Deleting user, please wait',
        modalType: ModalType.progress,
      };
    case FirmsActions.deleteFirmCompleted:
      return {
        ...state,
        firms: firms.filter((firm: Firm): boolean => firm.firmID !== data.firmID),
        taskMessage: 'Firm deleted successfully',
        modalType: ModalType.success,
      };
    case FirmsActions.closeTaskModal:
      return { ...state, taskMessage: null, modalType: ModalType.none };
    case FirmsActions.setSelectedBusinessUnit:
      return { ...state, selectedBusinessUnit: data };
    case FirmsActions.setIsConnectivity:
      return { ...state, isConnectivity: data };
    case FirmsActions.getFirmsTypesStarted:
      return { ...state, loadingFirmsTypes: true };
    case FirmsActions.getFirmsTypesCompleted:
      return { ...state, firmsTypes: data, loadingFirmsTypes: false };
    case FirmsActions.getFirmsTypesFailed:
      return { ...state, loadingFirmsTypes: false };
    case FirmsActions.setFirmsTypes:
      return { ...state, firmsTypes: data };
    default:
      return state;
  }
};

export default reducer;
